import { Button, Container, createTheme, Typography } from '@mui/material'
import { ThemeProvider } from '@mui/system'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

//hooks
import useAuthorization from 'hooks/useAuthorization'
import useQueryIsIPAddressBanned from '../../hooks/useQueryIsIPAddressBanned'
import useSegmentConfig from '../../hooks/useSegmentConfig'

//others
import BottomButton from '../../components/common/BottomButton'

import AuthorizationTerms from '../../components/AuthorizationTerms'
import { LOCAL_STORAGE, SEARCH_PARAMS } from '../../constants'
import { formatNumber } from '../../utils/utils'
import { NotFound } from '../index'

import './index.css'
import { UserByReferralIdResponse } from '@playback-rewards/shared-libraries'
import { AxiosResponse } from 'axios'
import useQueryGetReferralConfig from 'hooks/useQueryGetReferralConfig'
import { useQuery } from 'react-query'
import { getUserByReferralId } from 'services/referalService'
import QueryKeys from 'constants/queryKeys'
import { captureEvent } from '@sentry/react'

const HOW_IT_WORKS_ITEMS = [
  {
    title: 'Earn rewards by playing games',
    text: 'Playback lets you earn rewards for installing, playing, and making purchases in games',
  },
  {
    title: 'Turn rewards into gift cards',
    text: 'You can then redeem those rewards for gift cards to your favorite places like Amazon, Walmart, and PayPal',
  },
  {
    title: 'Get bonus rewards with first gift card',
    text: "Once you redeem your first gift card, you'll get a bonus $3 worth of rewards as a referral bonus",
  },
]

export const useQueryGetUserByReferralId = (referralId: string | undefined) => {
  const { data, isLoading, error } = useQuery<AxiosResponse<UserByReferralIdResponse, Error>>(
    [QueryKeys.GET_USER_BY_REFERRAL_ID, referralId],
    () => {
      return getUserByReferralId(referralId ?? '')
    },
    {
      enabled: Boolean(referralId),
      onError: (error) => {
        captureEvent({
          message: `Error fetching user by referral id ${referralId}`,
          level: 'error',
          extra: { err: error },
        })
      },
      retry: (failureCount, error: unknown) => {
        if (failureCount >= 3 || (error instanceof Error && error.message.includes('timeout'))) {
          return false
        }
        return true
      },
    }
  )

  return {
    referrerUser: data?.data,
    isLoadingReferrerUser: isLoading,
    error,
  }
}

const ReferralLanding = () => {
  const { isBanned } = useQueryIsIPAddressBanned()

  const { referralConfigData, isLoadingReferralConfig } = useQueryGetReferralConfig()
  const [size, setSize] = useState({ width: 0, height: 0 })
  const params = useParams<{ referralId?: string }>()
  const referralId = useMemo(() => {
    return (
      params.referralId ||
      new URLSearchParams(window.location.search).get('referrer_id') ||
      undefined
    )
  }, [params, window.location.search])
  const { referrerUser, isLoadingReferrerUser, error } = useQueryGetUserByReferralId(referralId)
  const configData = referrerUser?.userReferralConfig?.referralConfig ?? referralConfigData
  const navigate = useNavigate()

  const updateSize = () => setSize({ width: window.innerWidth, height: window.innerHeight })

  const getOfferData = async () => {
    sessionStorage.setItem(LOCAL_STORAGE.REFERRAL_ID, referralId || '')

    updateSize()
  }

  const { signupAttr, isLoading } = useSegmentConfig(getOfferData)
  const { handleSignupAuthorization } = useAuthorization(isBanned, signupAttr)

  useEffect(() => {
    window.addEventListener('resize', updateSize)

    return () => window.removeEventListener('resize', updateSize)
  }, [])

  const formattedCoins = formatNumber(configData?.refereeBonus?.bonusCoins || 0)
  const formattedPiggy = formatNumber(configData?.refereeBonus?.bonusPiggyBanks || 0)

  const navigateToAuthPage = (hasGmailAccount = true) => {
    if (hasGmailAccount) {
      return navigate('/auth')
    }
    return navigate(`/auth?${SEARCH_PARAMS.DONT_HAVE}=1&from=referral`)
  }

  if (error !== null || (!isLoadingReferrerUser && !referrerUser)) {
    return <NotFound />
  }

  return (
    <ThemeProvider theme={REFERRAL_LANDING_THEME}>
      <div
        className={`referralLandingInner ${
          isLoading || isLoadingReferrerUser || isLoadingReferralConfig ? 'blured' : ''
        }`}
      >
        <div className="referralLandingHeader">
          <img src="/images/playbackLogo.png" />
        </div>
        <div className="referralLandingEarn">
          <Container maxWidth="sm" sx={styles.earnContainer}>
            <Typography variant="title" sx={styles.earnTitle}>
              {referrerUser?.name} has invited you to Playback Rewards
            </Typography>
            <Typography variant="body1" sx={styles.earnText}>
              Earn ${configData?.refereeBonus?.bonusInUSD} when you sign up and redeem your first
              gift card!
            </Typography>
          </Container>
        </div>
        <div className="referralLandingJoin">
          <Container maxWidth="sm" sx={styles.joinContainer}>
            <Typography variant="title" sx={styles.joinTitle}>
              Join Today
            </Typography>
            <div>
              <img src="/images/continueWithGoogle.png" onClick={handleSignupAuthorization} />
              <Button sx={styles.joinDontHaveAccount}>
                <Typography
                  variant="body2"
                  sx={styles.joinDontHaveAccountText}
                  component="span"
                  onClick={() => navigateToAuthPage(false)}
                >
                  don't have a gmail account?
                </Typography>
              </Button>
            </div>
            <AuthorizationTerms />
          </Container>
        </div>
        <div
          className="referralLandingHow"
          style={{
            backgroundImage: 'url(/images/landingHowBackground.png)',
          }}
        >
          <Container maxWidth="lg" sx={styles.howContainer}>
            <Typography variant="title">How Playback Works</Typography>
            <div className="referralLandingHowItems">
              {HOW_IT_WORKS_ITEMS.map(({ title, text }, key) => (
                <div key={title} className="referralLandingHowItem">
                  <div className="referralLandingHowItemNumber">
                    <Typography variant="body1" sx={styles.howItemNumber}>
                      {key + 1}
                    </Typography>
                  </div>
                  <div className="referralLandingHowItemText">
                    <Typography variant="body1">{title}</Typography>
                    <Typography variant="body2">{text}</Typography>
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </div>
        <div className="referralLandingBonuses">
          <Container maxWidth="sm" sx={styles.bonusesContainer}>
            <Typography variant="title">Referral Bonuses</Typography>
            <Typography variant="body2" sx={styles.bonusesText}>
              Since{' '}
              <Typography variant="body2" component="strong" sx={styles.strong}>
                {referrerUser?.name}
              </Typography>{' '}
              referred you to Playback Rewards, you'll earn an additional $
              {configData?.refereeBonus?.bonusInUSD} worth of rewards with your first gift card
              redemption.
            </Typography>
            <div className="referralLandingBonusesFormula">
              {(configData?.refereeBonus?.bonusCoins ?? 0) > 0 && (
                <>
                  <div className="referralLandingBonusesInputInner">
                    <div className="referralLandingBonusesInputWrap coin">
                      <div className="referralLandingBonusesInput coin">
                        <Typography variant="title" sx={styles.value} className="priceText coin">
                          {formattedCoins}
                        </Typography>
                        <img src="/images/coin.png" className="referralLandingBonusesInputImage" />
                      </div>
                    </div>
                  </div>
                  <Typography variant="h3" sx={styles.bonusesPlus}>
                    +
                  </Typography>
                </>
              )}
              <div className="referralLandingBonusesInputInner">
                <div className="referralLandingBonusesInputWrap pig">
                  <div className="referralLandingBonusesInput pig">
                    <Typography variant="title" sx={styles.value} className="priceText pig">
                      {formattedPiggy}
                    </Typography>
                    <img src="/images/pig.png" className="referralLandingBonusesInputImage" />
                  </div>
                </div>
              </div>
            </div>
            <Typography variant="h3" sx={styles.bonusesEquals(size.width <= 640)}>
              =
            </Typography>
            <Typography variant="title" sx={styles.bonusesResult}>
              ${configData?.refereeBonus?.bonusInUSD} USD Value
            </Typography>
            <BottomButton style={styles.bonusesButton} handleClick={handleSignupAuthorization}>
              Get Started - It's Free
            </BottomButton>
          </Container>
        </div>
      </div>
    </ThemeProvider>
  )
}

const styles = {
  strong: {
    fontWeight: 700,
    fontSize: 'inherit',
  },
  earnContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '50px',
    paddingLeft: '50px',
    paddingRight: '50px',
    '@media(max-width: 640px)': {
      gap: '45px',
    },
    '@media(min-width: 600px)': {
      paddingLeft: '50px',
      paddingRight: '50px',
      maxWidth: '587px',
    },
  },
  earnTitle: {
    textTransform: 'uppercase',
    color: '#fff',
  },
  earnText: {
    color: '#fff',
  },
  joinTitle: {
    color: '#FFA703',
  },
  joinContainer: {
    maxWidth: '382px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    paddingRight: '32px',
    paddingLeft: '32px',
    img: {
      maxWidth: '331px',
      width: '100%',
      margin: 'auto',
      cursor: 'pointer',
      '@media(max-width: 640px)': {
        padding: '0 18px',
      },
    },
    '@media (min-width: 600px)': {
      maxWidth: '382px',
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  joinDontHaveAccount: {
    padding: '0',
    borderRadius: '100px',
    textDecoration: 'underline',
    textUnderlinePosition: 'under',
    textDecorationColor: '#000000AB',
    maxWidth: '331px',
    width: '100%',
    margin: 'auto',
    '&:hover': {
      textDecoration: 'underline',
      textDecorationColor: '#000000AB',
    },
  },
  joinDontHaveAccountText: {
    color: '#000000AB',
    textTransform: 'lowercase',
  },
  howContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '71px',
    '@media (max-width: 640px)': {
      gap: '29px',
    },
  },
  howItemNumber: {
    margin: 'auto',
    color: '#fff',
    fontWeight: 700,
    height: '100%',
    '@media (max-width: 640px)': {
      lineHeight: '32px',
    },
  },
  bonusesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '19px',
  },
  bonusesText: {
    maxWidth: '296px',
    margin: 'auto',
  },
  bonusesPlus: {
    margin: '10px -30px 0 0',
    width: '39px',
    '@media (max-width: 640px)': {
      margin: 'auto 0',
    },
  },
  bonusesEquals: (visible: boolean) => ({
    margin: 'auto 0',
    ...(!visible && { display: 'none' }),
  }),
  bonusesResult: {
    background: 'linear-gradient(180deg, #FFC52D 0%, #FF6492 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontWeight: 700,
    textTransform: 'uppercase',
    marginTop: '20px',
    '@media (max-width: 640px)': {
      marginTop: 0,
    },
  },
  bonusesButton: {
    padding: '14px 57px',
    width: 'max-content',
    margin: 'auto',
    textTransform: 'uppercase',
    marginTop: '29px',
    boxShadow: 'unset',
    background: '#FF8210',
  },
  footerLink: {
    cursor: 'pointer',
    textDecoration: 'underline',
    a: {
      color: '#fff',
      fontSize: '16px',
    },
  },
  value: {
    fontWeight: 700,
  },
}

const REFERRAL_LANDING_THEME = createTheme({
  typography: {
    title: {
      fontSize: '48px',
      lineHeight: '56px',
      fontWeight: 400,
      color: '#000000',
    },
    body1: {
      fontSize: '24px',
      fontWeight: 400,
      lineHeight: '32px',
      '@media (max-width: 640px)': {
        fontSize: '16px',
        lineHeight: '24px',
      },
    },
    body2: {
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: 400,
      '@media (max-width: 640px)': {
        fontSize: '12px',
        lineHeight: '16px',
      },
    },
    h3: {
      fontSize: '36px',
      lineHeight: '48px',
      fontWeight: 400,
      '@media (max-width: 640px)': {
        fontSize: '24px',
        lineHeight: '32px',
      },
    },
    button: {
      fontSize: '16px',
      lineHeight: '34px',
      fontWeight: 700,
    },
    footer: {
      fontSize: '16px',
      lineHeight: '22px',
      fontWeight: 700,
      color: '#fff',
      fontFamily: 'var(--open-sans-font)',
    },
    allVariants: {
      fontFamily: `"Poppins", sans-serif`,
      letterSpacing: 0,
      color: '#000000',
    },
  },

  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.MuiTypography-title': {
            '@media (max-width: 640px)': {
              fontSize: '36px',
              lineHeight: '40px',
            },
          },
        },
      },
    },
  },
})

export default ReferralLanding
